// src/registered/site-admin/report_management/blueprint/blueprintReportingTemplates.js
//
// Data-input templates for the "Conduct ESG Reporting" screen.
//
// Each entry is a *reporting topic* a company can sit down and fill in. A topic
// is a small tree:
//
//   topic
//     └── group   (a themed set of fields — renders as one fieldset card)
//           └── field  (one captured value)
//
// This mirrors the Report Component Library shape on purpose — a `group` is a
// component, a `field` is a slot — so these can later be promoted into
// ReportComponentDTO / ESG.ComponentDefinition rows without reshaping. Field
// `type` values are exactly SLOT_VALUE_TYPES from ReportComponentDTO:
//   number | text | richtext | boolean | enum | date
//
// Content is grounded in Arethia_Inspired_Vape_ESG_Blueprint.md:
//   §11.1 Scope 1 (factory fuels, company vehicles, refrigerants)
//   §11.2 Scope 2 (purchased electricity / heat / cooling)
//   §11.3 Scope 3 (inbound/outbound transport, business travel, commuting)
//   §12   Water (withdrawal, source, water stress, use split, discharge)
//   §19   Logistics & shipping (freight modes, tonne-km, methodology)
//   §23   ESG evidence model (period, owner, source, assurance, data quality…)

// ---------------------------------------------------------------------------
// shared building blocks
// ---------------------------------------------------------------------------

/** Methodology used to turn activity data into CO2e — reused by several groups. */
const METHODOLOGY_OPTIONS = [
  { label: "Supplier-specific / primary data", value: "SUPPLIER_SPECIFIC" },
  { label: "GHG Protocol + published emission factors", value: "GHG_FACTORS" },
  { label: "DEFRA / BEIS factors", value: "DEFRA" },
  { label: "GLEC Framework (freight)", value: "GLEC" },
  { label: "Spend-based estimate", value: "SPEND_BASED" },
  { label: "Other / mixed", value: "OTHER" },
];

const DATA_QUALITY_OPTIONS = [
  { label: "Measured / metered", value: "MEASURED" },
  { label: "Calculated from activity data", value: "CALCULATED" },
  { label: "Estimated", value: "ESTIMATED" },
  { label: "Proxy / industry average", value: "PROXY" },
];

// §23 — attached to every topic so each saved section is traceable to evidence.
const EVIDENCE_GROUP = {
  key: "evidence",
  label: "Evidence & assurance",
  help:
    "Applies to the whole section. Makes the numbers above traceable back to a "
    + "source, an owner and a review date (blueprint §23).",
  fields: [
    {
      key: "period_start",
      label: "Reporting period — start",
      type: "date",
      required: true,
      guidance: "First day of the period these figures cover.",
    },
    {
      key: "period_end",
      label: "Reporting period — end",
      type: "date",
      required: true,
      guidance: "Last day of the period these figures cover.",
    },
    {
      key: "boundary",
      label: "Reporting boundary / site(s)",
      type: "text",
      required: true,
      placeholder: "e.g. Reinbek production site, or Group consolidated",
      guidance:
        "Which legal entity / site / group this section is for. Water and energy "
        + "are most meaningful site-by-site (blueprint §12).",
    },
    {
      key: "data_owner",
      label: "Data owner",
      type: "text",
      required: true,
      placeholder: "Name or role responsible for these figures",
    },
    {
      key: "source_system",
      label: "Source system / records",
      type: "text",
      placeholder: "e.g. utility invoices, fleet card statements, BMS export",
    },
    {
      key: "supporting_document",
      label: "Supporting document reference",
      type: "text",
      placeholder: "Link or file reference held as evidence",
    },
    {
      key: "data_quality",
      label: "Overall data quality",
      type: "enum",
      required: true,
      options: DATA_QUALITY_OPTIONS,
    },
    {
      key: "externally_assured",
      label: "Externally assured?",
      type: "boolean",
      guidance: "Has a third party provided assurance over this data?",
    },
    {
      key: "last_reviewed",
      label: "Last reviewed",
      type: "date",
    },
    {
      key: "gaps",
      label: "Gaps / exceptions",
      type: "richtext",
      placeholder: "Anything missing, assumed, or not yet measurable.",
    },
    {
      key: "corrective_action",
      label: "Corrective action",
      type: "richtext",
      placeholder: "What will be done to close the gaps, and by when.",
    },
  ],
};

// ---------------------------------------------------------------------------
// 1. TRANSPORT
// ---------------------------------------------------------------------------

const TRANSPORT_TEMPLATE = {
  key: "transport",
  name: "Transport & Logistics Emissions",
  pillar: "E",
  topic: "Climate change — mobile combustion & transport",
  blueprintRefs: ["ESRS E1", "§11.1", "§11.3", "§19"],
  summary:
    "Fuel burned in owned/leased vehicles (Scope 1) plus freight, business "
    + "travel and commuting bought in from others (Scope 3).",
  guidance:
    "Enter activity data (litres, tonne-km, passenger-km) where you have it — "
    + "the CO2e boxes are optional and can be filled once a factor is applied.",
  groups: [
    {
      key: "own_fleet",
      label: "Own fleet & company vehicles (Scope 1)",
      help: "Vehicles you own or lease and fuel directly (blueprint §11.1).",
      fields: [
        { key: "diesel_litres", label: "Diesel used", type: "number", unit: "L", guidance: "Total litres across the fleet for the period." },
        { key: "petrol_litres", label: "Petrol used", type: "number", unit: "L" },
        { key: "cng_lpg_kg", label: "CNG / LPG used", type: "number", unit: "kg" },
        { key: "ev_kwh", label: "EV charging (from own supply)", type: "number", unit: "kWh", guidance: "Report the electricity under the Energy section too — note it here for context." },
        { key: "vehicle_count", label: "Vehicles in fleet", type: "number", unit: "count" },
        { key: "km_travelled", label: "Distance travelled", type: "number", unit: "km" },
        { key: "scope1_mobile_tco2e", label: "Scope 1 mobile CO2e", type: "number", unit: "tCO2e" },
      ],
    },
    {
      key: "freight",
      label: "Inbound & outbound freight (Scope 3)",
      help:
        "Goods moved by carriers on your behalf. Represent each leg as tonne-km "
        + "= tonnes carried × distance (blueprint §19).",
      fields: [
        { key: "inbound_sea_tkm", label: "Inbound — sea freight", type: "number", unit: "tonne-km" },
        { key: "inbound_road_tkm", label: "Inbound — road freight", type: "number", unit: "tonne-km" },
        { key: "inbound_air_tkm", label: "Inbound — air freight", type: "number", unit: "tonne-km" },
        { key: "outbound_road_tkm", label: "Outbound — road freight", type: "number", unit: "tonne-km" },
        { key: "outbound_sea_tkm", label: "Outbound — sea freight", type: "number", unit: "tonne-km" },
        { key: "outbound_air_tkm", label: "Outbound — air freight", type: "number", unit: "tonne-km" },
        { key: "primary_carrier", label: "Primary carrier(s)", type: "text", placeholder: "e.g. Maersk, DHL Freight" },
        { key: "freight_method", label: "Calculation methodology", type: "enum", options: METHODOLOGY_OPTIONS },
        { key: "scope3_freight_tco2e", label: "Freight CO2e", type: "number", unit: "tCO2e" },
      ],
    },
    {
      key: "travel_commuting",
      label: "Business travel & employee commuting (Scope 3)",
      help: "Trips not in your own vehicles (blueprint §11.3).",
      fields: [
        { key: "air_travel_pkm", label: "Air business travel", type: "number", unit: "passenger-km" },
        { key: "rail_travel_pkm", label: "Rail business travel", type: "number", unit: "passenger-km" },
        { key: "road_travel_pkm", label: "Road business travel (taxi / hire / private car)", type: "number", unit: "passenger-km" },
        { key: "hotel_nights", label: "Hotel nights", type: "number", unit: "nights" },
        { key: "commuting_basis", label: "Commuting estimate basis", type: "enum", options: [
          { label: "Employee survey", value: "SURVEY" },
          { label: "Headcount × average factor", value: "AVERAGE" },
          { label: "Not estimated this period", value: "NONE" },
        ] },
        { key: "commuting_pkm", label: "Employee commuting", type: "number", unit: "passenger-km" },
        { key: "scope3_travel_tco2e", label: "Travel & commuting CO2e", type: "number", unit: "tCO2e" },
      ],
    },
    {
      key: "transport_narrative",
      label: "Narrative & context",
      fields: [
        { key: "boundary_notes", label: "What is included / excluded", type: "richtext", placeholder: "e.g. excludes third-party courier last-mile; includes leased sales fleet." },
        { key: "reduction_actions", label: "Reduction actions taken or planned", type: "richtext", placeholder: "e.g. fleet electrification, modal shift sea vs air, route optimisation." },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------------------------------------------------------------------------
// 2. ENERGY (electricity, heating, lighting)
// ---------------------------------------------------------------------------

const ENERGY_TEMPLATE = {
  key: "energy",
  name: "Energy — Electricity, Heating & Lighting",
  pillar: "E",
  topic: "Climate change — energy consumption & purchased energy",
  blueprintRefs: ["ESRS E1-5", "§11.1", "§11.2", "§10"],
  summary:
    "Purchased electricity, heat and on-site fuel for heating and lighting. "
    + "Purchased energy is Scope 2; fuel burned on site is Scope 1.",
  guidance:
    "Take figures from utility invoices or meter reads for the period. Record "
    + "both location-based and market-based electricity CO2e if you can "
    + "(blueprint §10 highlights ~95–96% green electricity as an Arethia claim).",
  groups: [
    {
      key: "electricity",
      label: "Purchased electricity (Scope 2)",
      help: "Grid electricity bought for the site (blueprint §11.2).",
      fields: [
        { key: "electricity_total_kwh", label: "Total electricity purchased", type: "number", unit: "kWh", required: true },
        { key: "electricity_renewable_kwh", label: "Of which renewable / green tariff", type: "number", unit: "kWh" },
        { key: "renewable_pct", label: "Renewable share", type: "number", unit: "%", guidance: "Leave blank to derive from the two kWh figures." },
        { key: "electricity_supplier", label: "Electricity supplier", type: "text" },
        { key: "tariff_type", label: "Tariff type", type: "enum", options: [
          { label: "Standard grid", value: "STANDARD" },
          { label: "Green tariff (certificates / GoOs)", value: "GREEN_TARIFF" },
          { label: "Power purchase agreement (PPA)", value: "PPA" },
          { label: "Mixed", value: "MIXED" },
        ] },
        { key: "elec_location_tco2e", label: "Electricity CO2e — location-based", type: "number", unit: "tCO2e" },
        { key: "elec_market_tco2e", label: "Electricity CO2e — market-based", type: "number", unit: "tCO2e" },
      ],
    },
    {
      key: "heating",
      label: "Heating & on-site fuel combustion",
      help:
        "Natural gas / oil / LPG burned on site is Scope 1; purchased heat or "
        + "steam is Scope 2 (blueprint §11.1 / §11.2).",
      fields: [
        { key: "natural_gas_kwh", label: "Natural gas", type: "number", unit: "kWh" },
        { key: "heating_oil_litres", label: "Heating oil", type: "number", unit: "L" },
        { key: "lpg_kg", label: "LPG", type: "number", unit: "kg" },
        { key: "district_heat_kwh", label: "Purchased district heat / steam", type: "number", unit: "kWh" },
        { key: "purchased_cooling_kwh", label: "Purchased cooling", type: "number", unit: "kWh" },
        { key: "heat_pumps", label: "Heat pumps in use", type: "boolean", guidance: "Blueprint §10 lists heat pumps as an Arethia measure." },
        { key: "heat_recovery", label: "Heat recovery in use", type: "boolean" },
        { key: "scope1_stationary_tco2e", label: "Stationary combustion CO2e", type: "number", unit: "tCO2e" },
      ],
    },
    {
      key: "lighting_efficiency",
      label: "Lighting & building efficiency",
      help: "Context for the electricity figure — no separate meter is assumed.",
      fields: [
        { key: "lit_floor_area_m2", label: "Lit floor area", type: "number", unit: "m²" },
        { key: "led_coverage_pct", label: "LED coverage", type: "number", unit: "%" },
        { key: "lighting_controls", label: "Lighting controls", type: "enum", options: [
          { label: "None", value: "NONE" },
          { label: "Timers", value: "TIMERS" },
          { label: "Occupancy / daylight sensors", value: "SENSORS" },
          { label: "Building management system", value: "BMS" },
        ] },
        { key: "efficiency_notes", label: "Building efficiency notes", type: "richtext", placeholder: "e.g. high-efficiency building envelope, retrofit programme." },
      ],
    },
    {
      key: "self_generation",
      label: "On-site generation",
      help: "Blueprint §10 lists photovoltaic systems as an Arethia measure.",
      fields: [
        { key: "pv_generation_kwh", label: "PV generation", type: "number", unit: "kWh" },
        { key: "pv_self_consumed_kwh", label: "PV self-consumed", type: "number", unit: "kWh" },
        { key: "pv_exported_kwh", label: "PV exported to grid", type: "number", unit: "kWh" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------------------------------------------------------------------------
// 3. WATER
// ---------------------------------------------------------------------------

const WATER_TEMPLATE = {
  key: "water",
  name: "Water Withdrawal & Discharge",
  pillar: "E",
  topic: "Water & marine resources",
  blueprintRefs: ["ESRS E3", "§12"],
  summary:
    "Water drawn into the site, how it is used, whether the site is in a "
    + "water-stressed area, and what is discharged (blueprint §12).",
  guidance:
    "Track water site-by-site — the same volume has very different ESG "
    + "significance depending on local water stress (blueprint §12).",
  groups: [
    {
      key: "withdrawal",
      label: "Water withdrawal",
      help: "All water taken into the site during the period.",
      fields: [
        { key: "total_withdrawn_l", label: "Total water withdrawn", type: "number", unit: "L", required: true },
        { key: "municipal_l", label: "— Municipal / third-party supply", type: "number", unit: "L" },
        { key: "groundwater_l", label: "— Groundwater (own borehole)", type: "number", unit: "L" },
        { key: "surface_water_l", label: "— Surface water", type: "number", unit: "L" },
        { key: "rainwater_l", label: "— Harvested rainwater", type: "number", unit: "L" },
        { key: "primary_source", label: "Primary source", type: "enum", options: [
          { label: "Municipal / utility", value: "MUNICIPAL" },
          { label: "Groundwater", value: "GROUNDWATER" },
          { label: "Surface water", value: "SURFACE" },
          { label: "Rainwater harvesting", value: "RAINWATER" },
        ] },
      ],
    },
    {
      key: "water_stress",
      label: "Water stress",
      help:
        "Whether this site sits in a water-stressed basin, and how that was "
        + "assessed (blueprint §12).",
      fields: [
        { key: "in_water_stressed_area", label: "Site in a water-stressed area?", type: "boolean" },
        { key: "stress_basis", label: "Basis of assessment", type: "enum", options: [
          { label: "WRI Aqueduct", value: "WRI_AQUEDUCT" },
          { label: "WWF Water Risk Filter", value: "WWF" },
          { label: "Local regulator classification", value: "REGULATOR" },
          { label: "Other / internal", value: "OTHER" },
        ] },
        { key: "stressed_area_withdrawal_l", label: "Withdrawal in water-stressed areas", type: "number", unit: "L" },
      ],
    },
    {
      key: "use_split",
      label: "Water use breakdown",
      help: "How withdrawn water is used on site (blueprint §12).",
      fields: [
        { key: "process_water_l", label: "Process water", type: "number", unit: "L" },
        { key: "cleaning_water_l", label: "Cleaning / CIP water", type: "number", unit: "L" },
        { key: "sanitation_water_l", label: "Sanitation / domestic water", type: "number", unit: "L" },
        { key: "consumed_l", label: "Water consumed (not returned)", type: "number", unit: "L", guidance: "Evaporation, product content, irrigation — water that does not leave as discharge." },
      ],
    },
    {
      key: "discharge",
      label: "Wastewater & discharge",
      help: "Water leaving the site and how it is treated (blueprint §12).",
      fields: [
        { key: "wastewater_l", label: "Wastewater discharged", type: "number", unit: "L" },
        { key: "treatment_method", label: "Treatment method", type: "enum", options: [
          { label: "On-site treatment plant", value: "ONSITE" },
          { label: "Discharge to municipal sewer", value: "SEWER" },
          { label: "Licensed contractor removal", value: "CONTRACTOR" },
          { label: "No treatment", value: "NONE" },
        ] },
        { key: "discharge_destination", label: "Discharge destination", type: "text", placeholder: "e.g. municipal WWTP, river, sea outfall" },
        { key: "water_quality_measurements", label: "Key water-quality measurements", type: "richtext", placeholder: "e.g. COD, BOD, pH, suspended solids, nitrogen — with values and limits." },
        { key: "incidents", label: "Discharge incidents / breaches", type: "richtext", placeholder: "Any permit exceedances or spills in the period." },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------------------------------------------------------------------------
// ESRS topical standards — one topic per standard area, so the pillar pages
// lead a company through everything it may have to submit. Activity data
// first; narrative last; every topic ends with the evidence group.
// ---------------------------------------------------------------------------

const YES_NO_PARTIAL = [
  { label: "Yes", value: "YES" },
  { label: "Partly", value: "PARTLY" },
  { label: "No", value: "NO" },
];

const narrative = (key, label, fields) => ({ key, label, fields });

// ---------- ENVIRONMENT ----------

const GHG_TEMPLATE = {
  key: "ghg_emissions",
  name: "GHG Emissions Summary",
  pillar: "E",
  topic: "Climate change — gross Scope 1, 2 and 3",
  blueprintRefs: ["ESRS E1-6"],
  summary: "The company's total greenhouse-gas footprint by scope, plus targets and intensity.",
  guidance: "Totals here roll up the Transport and Energy sections; enter them directly if you calculate elsewhere.",
  groups: [
    {
      key: "scopes",
      label: "Gross emissions by scope",
      fields: [
        { key: "scope1_tco2e", label: "Scope 1 — direct", type: "number", unit: "tCO2e", required: true },
        { key: "scope2_location_tco2e", label: "Scope 2 — location-based", type: "number", unit: "tCO2e", required: true },
        { key: "scope2_market_tco2e", label: "Scope 2 — market-based", type: "number", unit: "tCO2e" },
        { key: "scope3_tco2e", label: "Scope 3 — value chain (total)", type: "number", unit: "tCO2e" },
        { key: "scope3_categories", label: "Scope 3 categories included", type: "text", placeholder: "e.g. 1, 4, 6, 7, 9" },
        { key: "biogenic_tco2", label: "Biogenic CO2 (reported separately)", type: "number", unit: "tCO2" },
        { key: "methodology", label: "Calculation methodology", type: "enum", options: METHODOLOGY_OPTIONS },
      ],
    },
    {
      key: "intensity_targets",
      label: "Intensity & targets",
      fields: [
        { key: "net_revenue", label: "Net revenue (for intensity)", type: "number", unit: "EUR" },
        { key: "intensity_tco2e_per_meur", label: "GHG intensity", type: "number", unit: "tCO2e / EUR m" },
        { key: "base_year", label: "Base year", type: "number" },
        { key: "reduction_target_pct", label: "Reduction target vs base year", type: "number", unit: "%" },
        { key: "target_year", label: "Target year", type: "number" },
        { key: "transition_plan", label: "Transition plan in place?", type: "enum", options: YES_NO_PARTIAL },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const POLLUTION_TEMPLATE = {
  key: "pollution",
  name: "Pollution of Air, Water & Soil",
  pillar: "E",
  topic: "Pollution — emissions to air, water and soil; substances of concern",
  blueprintRefs: ["ESRS E2"],
  summary: "Pollutants released by your operations and any substances of (very high) concern you produce or use.",
  guidance: "Report pollutants above E-PRTR thresholds where you measure them; otherwise describe what you monitor.",
  groups: [
    {
      key: "air",
      label: "Emissions to air",
      fields: [
        { key: "nox_kg", label: "Nitrogen oxides (NOx)", type: "number", unit: "kg" },
        { key: "sox_kg", label: "Sulphur oxides (SOx)", type: "number", unit: "kg" },
        { key: "pm10_kg", label: "Particulate matter (PM10)", type: "number", unit: "kg" },
        { key: "voc_kg", label: "Volatile organic compounds", type: "number", unit: "kg" },
      ],
    },
    {
      key: "water_soil",
      label: "Emissions to water & soil",
      fields: [
        { key: "water_pollutants", label: "Pollutants to water (substance, amount)", type: "richtext" },
        { key: "soil_pollutants", label: "Pollutants to soil (substance, amount)", type: "richtext" },
        { key: "microplastics_kg", label: "Microplastics generated or used", type: "number", unit: "kg" },
      ],
    },
    {
      key: "substances",
      label: "Substances of concern",
      fields: [
        { key: "soc_tonnes", label: "Substances of concern produced / used", type: "number", unit: "t" },
        { key: "svhc_tonnes", label: "Of which very high concern (SVHC)", type: "number", unit: "t" },
        { key: "incidents", label: "Pollution incidents / permit breaches", type: "richtext" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const BIODIVERSITY_TEMPLATE = {
  key: "biodiversity",
  name: "Biodiversity & Ecosystems",
  pillar: "E",
  topic: "Biodiversity — sites near sensitive areas, land use, impacts",
  blueprintRefs: ["ESRS E4"],
  summary: "Where your sites sit relative to protected or sensitive areas, and how you use land.",
  guidance: "Start with a site list: which sites are in or next to biodiversity-sensitive areas.",
  groups: [
    {
      key: "sites",
      label: "Sites & sensitive areas",
      fields: [
        { key: "site_count", label: "Operational sites", type: "number", unit: "count" },
        { key: "sensitive_site_count", label: "Sites in / near sensitive areas", type: "number", unit: "count", required: true },
        { key: "sensitive_area_ha", label: "Area of those sites", type: "number", unit: "ha" },
        { key: "site_list", label: "Which sites and which areas", type: "richtext", placeholder: "e.g. Natura 2000, UNESCO, Key Biodiversity Areas" },
      ],
    },
    {
      key: "land_use",
      label: "Land use",
      fields: [
        { key: "total_land_ha", label: "Total land use", type: "number", unit: "ha" },
        { key: "sealed_area_ha", label: "Sealed (built / paved) area", type: "number", unit: "ha" },
        { key: "nature_oriented_ha", label: "Nature-oriented area on site", type: "number", unit: "ha" },
      ],
    },
    narrative("bio_narrative", "Impacts & actions", [
      { key: "impacts", label: "Material impacts on ecosystems", type: "richtext" },
      { key: "actions", label: "Protection / restoration actions", type: "richtext" },
    ]),
    EVIDENCE_GROUP,
  ],
};

const RESOURCES_TEMPLATE = {
  key: "resources_waste",
  name: "Resource Use & Waste",
  pillar: "E",
  topic: "Circular economy — material inflows, outflows and waste",
  blueprintRefs: ["ESRS E5"],
  summary: "Materials you bring in, products and packaging going out, and the waste you generate.",
  guidance: "Waste figures usually come from your waste contractor's annual statement.",
  groups: [
    {
      key: "inflows",
      label: "Material inflows",
      fields: [
        { key: "materials_t", label: "Total materials used", type: "number", unit: "t" },
        { key: "recycled_input_pct", label: "Recycled / reused input share", type: "number", unit: "%" },
        { key: "renewable_input_pct", label: "Sustainably sourced biological input share", type: "number", unit: "%" },
        { key: "packaging_t", label: "Packaging placed on the market", type: "number", unit: "t" },
      ],
    },
    {
      key: "waste",
      label: "Waste generated",
      fields: [
        { key: "waste_total_t", label: "Total waste", type: "number", unit: "t", required: true },
        { key: "hazardous_t", label: "Hazardous waste", type: "number", unit: "t" },
        { key: "recycled_t", label: "Diverted — recycled / reused", type: "number", unit: "t" },
        { key: "incinerated_t", label: "Directed to incineration", type: "number", unit: "t" },
        { key: "landfill_t", label: "Directed to landfill", type: "number", unit: "t" },
        { key: "waste_contractor", label: "Waste contractor(s)", type: "text" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------- SOCIAL ----------

const WORKFORCE_TEMPLATE = {
  key: "own_workforce",
  name: "Own Workforce",
  pillar: "S",
  hr: true,
  topic: "Headcount, contracts, diversity, pay and training",
  blueprintRefs: ["ESRS S1-6", "S1-9", "S1-13", "S1-16"],
  summary: "Who works for you: headcount by contract and gender, turnover, pay gap and training.",
  guidance: "Headcount at period end unless stated; HR/payroll exports are the usual source.",
  groups: [
    {
      key: "headcount",
      label: "Headcount & contracts",
      fields: [
        { key: "headcount_total", label: "Total employees", type: "number", unit: "headcount", required: true },
        { key: "headcount_female", label: "— Female", type: "number", unit: "headcount" },
        { key: "headcount_male", label: "— Male", type: "number", unit: "headcount" },
        { key: "permanent", label: "Permanent contracts", type: "number", unit: "headcount" },
        { key: "temporary", label: "Temporary contracts", type: "number", unit: "headcount" },
        { key: "leavers", label: "Employees who left", type: "number", unit: "headcount" },
        { key: "turnover_pct", label: "Turnover rate", type: "number", unit: "%" },
      ],
    },
    {
      key: "pay_training",
      label: "Pay & training",
      fields: [
        { key: "gender_pay_gap_pct", label: "Gender pay gap", type: "number", unit: "%" },
        { key: "pay_ratio", label: "Highest-paid to median pay ratio", type: "number" },
        { key: "collective_bargaining_pct", label: "Covered by collective bargaining", type: "number", unit: "%" },
        { key: "training_hours_avg", label: "Average training hours per employee", type: "number", unit: "h" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const HEALTH_SAFETY_TEMPLATE = {
  key: "health_safety",
  name: "Health & Safety",
  pillar: "S",
  hr: true,
  topic: "Work-related injuries, ill health and fatalities",
  blueprintRefs: ["ESRS S1-14"],
  summary: "Accidents, lost days and the coverage of your health & safety management system.",
  guidance: "Rate per million hours worked; include contractors working on your sites where you can.",
  groups: [
    {
      key: "incidents",
      label: "Incidents",
      fields: [
        { key: "fatalities", label: "Fatalities", type: "number", unit: "count", required: true },
        { key: "recordable_injuries", label: "Recordable work-related accidents", type: "number", unit: "count", required: true },
        { key: "hours_worked", label: "Hours worked", type: "number", unit: "h" },
        { key: "injury_rate", label: "Recordable accident rate", type: "number", unit: "per 1m h" },
        { key: "days_lost", label: "Days lost to injury / ill health", type: "number", unit: "days" },
        { key: "ill_health_cases", label: "Cases of work-related ill health", type: "number", unit: "count" },
      ],
    },
    {
      key: "system",
      label: "Management system",
      fields: [
        { key: "hs_system_coverage_pct", label: "Workforce covered by an H&S system", type: "number", unit: "%" },
        { key: "iso45001", label: "ISO 45001 certified?", type: "boolean" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const TRAINING_TEMPLATE = {
  key: "training",
  name: "Training & Development",
  pillar: "S",
  topic: "Skills, training hours and performance reviews",
  blueprintRefs: ["ESRS S1-13"],
  hr: true,
  summary: "How much your people are trained and developed, and how many have regular reviews.",
  guidance: "Learning-system or HR records; can be filled from HR & Workforce.",
  groups: [
    {
      key: "programmes",
      label: "Training & reviews",
      fields: [
        { key: "training_hours_total", label: "Training hours (total)", type: "number", unit: "h" },
        { key: "training_hours_avg", label: "Training hours per employee", type: "number", unit: "h", required: true },
        { key: "employees_trained_pct", label: "Employees who received training", type: "number", unit: "%" },
        { key: "performance_reviews_pct", label: "Employees with a performance review", type: "number", unit: "%" },
      ],
    },
    narrative("training_narrative", "Programmes", [
      { key: "programmes", label: "Main training and development programmes", type: "richtext" },
    ]),
    EVIDENCE_GROUP,
  ],
};

const RECRUITMENT_TEMPLATE = {
  key: "recruitment",
  name: "Recruitment & Hiring",
  pillar: "S",
  topic: "New hires, open roles and fair recruitment",
  blueprintRefs: ["ESRS S1-6"],
  hr: true,
  summary: "Who you hired in the period, how long hiring took, and how you keep recruitment fair.",
  guidance: "HR or applicant-tracking records; can be filled from HR & Workforce.",
  groups: [
    {
      key: "hiring",
      label: "Hiring",
      fields: [
        { key: "new_hires", label: "New hires", type: "number", unit: "headcount", required: true },
        { key: "new_hires_female", label: "New hires — female", type: "number", unit: "headcount" },
        { key: "open_positions", label: "Open positions at period end", type: "number", unit: "count" },
        { key: "avg_time_to_hire_days", label: "Average time to hire", type: "number", unit: "days" },
        { key: "fair_recruitment_policy", label: "Fair / non-discriminatory recruitment policy?", type: "enum", options: YES_NO_PARTIAL },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const VALUE_CHAIN_TEMPLATE = {
  key: "value_chain_workers",
  name: "Workers in the Value Chain",
  pillar: "S",
  topic: "Supplier labour practices and human-rights due diligence",
  blueprintRefs: ["ESRS S2"],
  summary: "How you check working conditions at suppliers and what you found.",
  guidance: "Describe your supplier code, audits and any issues raised during the period.",
  groups: [
    {
      key: "due_diligence",
      label: "Due diligence",
      fields: [
        { key: "supplier_code", label: "Supplier code of conduct in place?", type: "enum", options: YES_NO_PARTIAL, required: true },
        { key: "suppliers_assessed", label: "Suppliers assessed / audited", type: "number", unit: "count" },
        { key: "high_risk_suppliers", label: "Suppliers identified as high risk", type: "number", unit: "count" },
        { key: "grievance_channel", label: "Grievance channel open to supplier workers?", type: "boolean" },
      ],
    },
    narrative("vc_narrative", "Findings & actions", [
      { key: "issues_found", label: "Issues found (child / forced labour, wages, safety…)", type: "richtext" },
      { key: "remediation", label: "Remediation taken", type: "richtext" },
    ]),
    EVIDENCE_GROUP,
  ],
};

const COMMUNITIES_TEMPLATE = {
  key: "communities",
  name: "Affected Communities",
  pillar: "S",
  topic: "Impacts on local communities around your sites",
  blueprintRefs: ["ESRS S3"],
  summary: "Engagement with, impacts on and investment in the communities near your operations.",
  guidance: "Focus on communities materially affected by your sites or supply chain.",
  groups: [
    {
      key: "engagement",
      label: "Engagement & investment",
      fields: [
        { key: "engagement_process", label: "Community engagement process in place?", type: "enum", options: YES_NO_PARTIAL },
        { key: "complaints", label: "Community complaints received", type: "number", unit: "count" },
        { key: "community_investment_eur", label: "Community investment / donations", type: "number", unit: "EUR" },
      ],
    },
    narrative("comm_narrative", "Impacts & actions", [
      { key: "impacts", label: "Material impacts on communities", type: "richtext" },
      { key: "actions", label: "Actions taken", type: "richtext" },
    ]),
    EVIDENCE_GROUP,
  ],
};

const CONSUMERS_TEMPLATE = {
  key: "consumers",
  name: "Consumers & End-users",
  pillar: "S",
  topic: "Product safety, information and data privacy",
  blueprintRefs: ["ESRS S4"],
  summary: "Safety and responsible marketing of your products, and protection of customer data.",
  guidance: "Include product recalls, safety incidents and data-protection breaches in the period.",
  groups: [
    {
      key: "safety_privacy",
      label: "Safety & privacy",
      fields: [
        { key: "recalls", label: "Product recalls", type: "number", unit: "count" },
        { key: "safety_incidents", label: "Product safety incidents", type: "number", unit: "count" },
        { key: "data_breaches", label: "Customer data breaches", type: "number", unit: "count" },
        { key: "complaints", label: "Consumer complaints", type: "number", unit: "count" },
        { key: "marketing_policy", label: "Responsible-marketing policy in place?", type: "enum", options: YES_NO_PARTIAL },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------- GOVERNANCE ----------

const GOVERNANCE_BODIES_TEMPLATE = {
  key: "governance_bodies",
  name: "Board & Oversight",
  pillar: "G",
  topic: "Administrative, management and supervisory bodies",
  blueprintRefs: ["ESRS 2 GOV-1", "GOV-3"],
  summary: "Board composition, independence, sustainability expertise and ESG-linked pay.",
  guidance: "As at period end.",
  groups: [
    {
      key: "board",
      label: "Board composition",
      fields: [
        { key: "members", label: "Board members", type: "number", unit: "count", required: true },
        { key: "female_pct", label: "Female members", type: "number", unit: "%" },
        { key: "independent_pct", label: "Independent members", type: "number", unit: "%" },
        { key: "esg_expertise", label: "Sustainability expertise on the board?", type: "boolean" },
        { key: "esg_linked_pay", label: "Executive pay linked to ESG targets?", type: "enum", options: YES_NO_PARTIAL },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const BUSINESS_CONDUCT_TEMPLATE = {
  key: "business_conduct",
  name: "Business Conduct & Anti-corruption",
  pillar: "G",
  topic: "Culture, whistle-blowing, corruption and bribery",
  blueprintRefs: ["ESRS G1-1", "G1-3", "G1-4"],
  summary: "Code of conduct, whistle-blower protection, anti-corruption training and confirmed incidents.",
  guidance: "Count confirmed incidents only; describe open investigations in the narrative.",
  groups: [
    {
      key: "conduct",
      label: "Policies & training",
      fields: [
        { key: "code_of_conduct", label: "Code of conduct in place?", type: "enum", options: YES_NO_PARTIAL, required: true },
        { key: "whistleblower_channel", label: "Whistle-blower channel in place?", type: "boolean" },
        { key: "at_risk_trained_pct", label: "At-risk functions trained on anti-corruption", type: "number", unit: "%" },
      ],
    },
    {
      key: "incidents",
      label: "Incidents",
      fields: [
        { key: "corruption_convictions", label: "Convictions for corruption / bribery", type: "number", unit: "count" },
        { key: "fines_eur", label: "Fines for corruption / bribery", type: "number", unit: "EUR" },
        { key: "confirmed_incidents", label: "Confirmed incidents", type: "number", unit: "count" },
        { key: "political_contributions_eur", label: "Political contributions", type: "number", unit: "EUR" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

const PAYMENT_PRACTICES_TEMPLATE = {
  key: "payment_practices",
  name: "Supplier Payment Practices",
  pillar: "G",
  topic: "Payment terms and late payment to suppliers",
  blueprintRefs: ["ESRS G1-6"],
  summary: "How quickly you pay suppliers, especially SMEs, and any late-payment proceedings.",
  guidance: "Your accounts-payable ledger is the source — a good candidate for a ledger mapping.",
  groups: [
    {
      key: "payments",
      label: "Payment performance",
      fields: [
        { key: "avg_days_to_pay", label: "Average days to pay an invoice", type: "number", unit: "days", required: true },
        { key: "standard_terms_days", label: "Standard payment terms", type: "number", unit: "days" },
        { key: "paid_on_terms_pct", label: "Payments made within terms", type: "number", unit: "%" },
        { key: "late_payment_proceedings", label: "Legal proceedings for late payment", type: "number", unit: "count" },
      ],
    },
    EVIDENCE_GROUP,
  ],
};

// ---------------------------------------------------------------------------
// registry — order within a pillar is the order shown on its page
// ---------------------------------------------------------------------------

export const REPORTING_TEMPLATES = [
  GHG_TEMPLATE,
  TRANSPORT_TEMPLATE,
  ENERGY_TEMPLATE,
  POLLUTION_TEMPLATE,
  WATER_TEMPLATE,
  BIODIVERSITY_TEMPLATE,
  RESOURCES_TEMPLATE,
  WORKFORCE_TEMPLATE,
  HEALTH_SAFETY_TEMPLATE,
  TRAINING_TEMPLATE,
  RECRUITMENT_TEMPLATE,
  VALUE_CHAIN_TEMPLATE,
  COMMUNITIES_TEMPLATE,
  CONSUMERS_TEMPLATE,
  GOVERNANCE_BODIES_TEMPLATE,
  BUSINESS_CONDUCT_TEMPLATE,
  PAYMENT_PRACTICES_TEMPLATE,
];

/** One colour per pillar, used on pillar buttons, topic panels and form groups. */
export const PILLAR_COLORS = {
  E: { main: "#198754", soft: "#e9f5ee" },
  S: { main: "#0b7fab", soft: "#e6f3f8" },
  G: { main: "#b07d00", soft: "#fbf4e2" },
  GEN: { main: "#6c757d", soft: "#f1f3f5" },
};

/** The three pillar pages, in order. */
export const PILLARS = [
  { key: "E", label: "Environmental", icon: "bi-tree", blurb: "Climate, energy, transport, pollution, water, biodiversity and resources (ESRS E1–E5)." },
  { key: "S", label: "Social", icon: "bi-people", blurb: "Your workforce, health & safety, value-chain workers, communities and consumers (ESRS S1–S4)." },
  { key: "G", label: "Governance", icon: "bi-bank", blurb: "Board oversight, business conduct, anti-corruption and payment practices (ESRS 2 / G1)." },
];

export const PILLAR_LABELS = {
  E: "Environmental",
  S: "Social",
  G: "Governance",
  GEN: "General",
};

export const templateByKey = (key) =>
  REPORTING_TEMPLATES.find((t) => t.key === key) || null;

/** Every field in a topic, flattened, keyed "<groupKey>.<fieldKey>". */
export const flattenFields = (template) => {
  if (!template) return [];
  const out = [];
  template.groups.forEach((g) => {
    g.fields.forEach((f) => {
      out.push({ ...f, groupKey: g.key, groupLabel: g.label, path: `${g.key}.${f.key}` });
    });
  });
  return out;
};

/** Count of capturable fields — shown on the picker card. */
export const fieldCount = (template) =>
  (template?.groups || []).reduce((n, g) => n + g.fields.length, 0);

/** Blank value map for a fresh submission. */
export const emptyValues = (template) => {
  const v = {};
  flattenFields(template).forEach((f) => {
    v[f.path] = f.type === "boolean" ? false : "";
  });
  return v;
};

/** Required paths that are still empty — used to gate "mark complete". */
export const missingRequired = (template, values) =>
  flattenFields(template)
    .filter((f) => f.required)
    .filter((f) => {
      const val = values?.[f.path];
      return val === "" || val === null || val === undefined;
    })
    .map((f) => `${f.groupLabel} — ${f.label}`);
